import { useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { getManifest } from '@senses/games'

import { ProgressChart, type ChartPoint } from '../components/ProgressChart.tsx'
import { SessionTable } from '../components/SessionTable.tsx'
import { Alert, Empty, Spinner } from '../components/ui.tsx'
import { api, type ProgressPoint } from '../lib/api.ts'
import { useAsync } from '../lib/hooks.ts'
import { formatChange, formatNumber, plural } from '../lib/format.ts'

const RANGES = [
  { days: 30, label: '30 dnů' },
  { days: 90, label: '3 měsíce' },
  { days: 365, label: 'rok' },
  { days: 3650, label: 'vše' },
] as const

/** Hodnota záložky „napříč programy". */
const ALL = 'all'

/** Vývoj výkonu v jedné hře. Trenér sem přejde s `?userId=` konkrétního klienta. */
export function ProgressPage(): React.JSX.Element {
  const { gameId = '' } = useParams()
  const [search] = useSearchParams()
  const userId = search.get('userId') ? Number(search.get('userId')) : undefined

  const [days, setDays] = useState<number>(365)
  const [mode, setMode] = useState<'sessions' | 'daily'>('sessions')
  const [tab, setTab] = useState<string | null>(null)
  // Propojení grafu a výpisu: najetí na bod podbarví řádek a naopak,
  // kliknutí na bod otevře detail té série.
  const [highlightId, setHighlightId] = useState<number | null>(null)
  const [detailId, setDetailId] = useState<number | null>(null)

  const manifest = getManifest(gameId)
  const metric = manifest?.primaryMetric

  const progress = useAsync(() => api.sessions.progress(gameId, userId, days), [gameId, userId, days])
  const allPoints = useMemo(() => progress.data?.points ?? [], [progress.data])

  // Výpis sérií se načítá zvlášť, protože na rozdíl od grafu obsahuje
  // i nedokončené tréninky — ty do křivky nepatří, ale vědět o nich je dobré.
  const history = useAsync(
    () => api.sessions.list({ userId, gameId, limit: 200 }),
    [userId, gameId],
  )

  /* Programy, které klient v této hře opravdu odehrál. */
  const programs = useMemo(() => groupByProgram(allPoints), [allPoints])

  // Dokud si klient nevybere, ukáže se ten nejvíc trénovaný program.
  const activeTab = tab ?? programs[0]?.key ?? ALL
  const points = useMemo(
    () =>
      activeTab === ALL
        ? allPoints
        : allPoints.filter((p) => programKey(p) === activeTab),
    [allPoints, activeTab],
  )

  // Denní průměry se počítají až tady, protože závisí na zvolené záložce.
  const daily = useMemo(() => aggregateByDay(points), [points])

  const sessions = useMemo(() => {
    const all = history.data?.sessions ?? []
    if (activeTab === ALL) return all
    return all.filter((s) => (s.programId === null ? 'none' : String(s.programId)) === activeTab)
  }, [history.data, activeTab])

  const chartPoints: ChartPoint[] =
    mode === 'sessions'
      ? points.map((p) => ({
          id: p.id,
          date: p.finishedAt,
          value: p.score,
          label: activeTab === ALL ? p.programName : null,
        }))
      : daily.map((d) => ({
          date: d.day,
          value: d.avgScore,
          label: `${d.sessions} ${plural(d.sessions, 'série', 'série', 'sérií')}`,
        }))

  const scores = points.map((p) => p.score)
  const first = scores[0]
  const last = scores.at(-1)
  const best = scores.length > 0 ? Math.max(...scores) : null
  const change = first !== undefined && last !== undefined ? formatChange(first, last) : null

  return (
    <main className="page">
      <div className="page__header">
        <div>
          <h1>{manifest?.name ?? gameId}</h1>
          <p>
            {metric ? `Sleduje se ${metric.label.toLowerCase()}` : 'Vývoj výsledků'}
            {metric?.higherIsBetter === false ? ' — nižší hodnota je lepší.' : ' — vyšší hodnota je lepší.'}
          </p>
        </div>

        <div className="page__actions">
          <select value={mode} onChange={(e) => setMode(e.target.value as 'sessions' | 'daily')}>
            <option value="sessions">Každá série</option>
            <option value="daily">Průměr za den</option>
          </select>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {RANGES.map((range) => (
              <option key={range.days} value={range.days}>
                {range.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {progress.error && <Alert>{progress.error}</Alert>}
      {progress.loading && <Spinner />}

      {progress.data && allPoints.length === 0 && (
        <Empty title="Za zvolené období nejsou žádná data">
          <p>Zkus delší časový rozsah nebo nejdřív odtrénuj sérii.</p>
        </Empty>
      )}

      {/* Každý program má jiné nastavení, takže jeho výsledky tvoří vlastní řadu.
          Společná křivka by ukazovala hlavně to, co klient zrovna hrál. */}
      {programs.length > 1 && (
        <div className="topbar__nav" style={{ marginBottom: '1.5rem' }}>
          {programs.map((program) => (
            <TabButton
              key={program.key}
              active={activeTab === program.key}
              onClick={() => setTab(program.key)}
              label={program.name}
              count={program.count}
            />
          ))}
          <TabButton
            active={activeTab === ALL}
            onClick={() => setTab(ALL)}
            label="Napříč programy"
            count={allPoints.length}
          />
        </div>
      )}

      {allPoints.length > 0 && scores.length > 0 && (
        <>
          {activeTab === ALL && programs.length > 1 && (
            <Alert kind="info">
              Graf míchá {programs.length} různá nastavení. {metric?.label ?? 'Metrika'} je sice
              navržená tak, aby šla srovnávat napříč obtížnostmi, ale na posouzení vlastního
              zlepšení je spolehlivější jeden konkrétní program.
            </Alert>
          )}

          <div className="stats" style={{ marginBottom: '1.75rem' }}>
            <div className="stat">
              <div className="stat__label">Aktuální</div>
              <div className="stat__value">{formatNumber(last!, metric?.precision ?? 2)}</div>
            </div>
            <div className="stat">
              <div className="stat__label">Nejlepší</div>
              <div className="stat__value">{formatNumber(best!, metric?.precision ?? 2)}</div>
            </div>
            {/* Posun napříč programy nic neříká: střídání lehčího a těžšího
                nastavení vyrobí klesající trend i u klienta, který se v obou
                programech lepší. Místo zavádějícího čísla se ukáže počet
                programů, mezi kterými se přepíná. */}
            {activeTab === ALL && programs.length > 1 ? (
              <div className="stat">
                <div className="stat__label">Programů</div>
                <div className="stat__value">{programs.length}</div>
              </div>
            ) : (
              <div className="stat">
                <div className="stat__label">Posun</div>
                <div className={`stat__value ${last! >= first! ? 'trend--up' : 'trend--down'}`}>
                  {change ?? '—'}
                </div>
              </div>
            )}
            <div className="stat">
              <div className="stat__label">Sérií</div>
              <div className="stat__value">{scores.length}</div>
            </div>
          </div>

          <div className="card">
            <ProgressChart
              points={chartPoints}
              precision={metric?.precision ?? 2}
              highlightId={highlightId}
              onHover={setHighlightId}
              onSelect={setDetailId}
            />
          </div>

          <p className="field__help" style={{ marginTop: '1rem' }}>
            Přerušovaná čára je lineární trend přes celé období. Krátkodobé výkyvy jsou normální —
            výkon kolísá podle únavy i denní doby, rozhoduje směr trendu.
            {mode === 'sessions' && ' Najetím na bod se podbarví odpovídající série ve výpisu, kliknutím se otevře její rozbor.'}
          </p>
        </>
      )}

      {/* Výpis těch samých sérií, ze kterých je graf. Filtruje se stejnou
          záložkou, takže tabulka a křivka vždy odpovídají jedna druhé. */}
      <section className="section">
        <div className="section__title">
          <h2>Odtrénované série</h2>
          <span className="dim">{sessions.length}</span>
        </div>

        <SessionTable
          sessions={sessions}
          loading={history.loading}
          error={history.error}
          showProgram={activeTab === ALL}
          highlightId={highlightId}
          onHover={setHighlightId}
          detailId={detailId}
          onDetailChange={setDetailId}
          emptyTitle="Zatím žádné série"
          emptyHint="Jakmile dokončíš trénink, objeví se tady i s podrobným rozborem."
        />
      </section>
    </main>
  )
}

function TabButton({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  label: string
  count: number
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={`topbar__link ${active ? 'topbar__link--active' : ''}`}
      onClick={onClick}
      style={{ border: 'none', background: active ? undefined : 'transparent', cursor: 'pointer' }}
    >
      {label} <span className="dim">{count}</span>
    </button>
  )
}

/* ------------------------------------------------------------------ */

interface ProgramTab {
  key: string
  name: string
  count: number
}

/** Série odehrané mimo program (zkouška trenérem) spadnou do jedné skupiny. */
function programKey(point: ProgressPoint): string {
  return point.programId === null ? 'none' : String(point.programId)
}

/** Programy seřazené od nejvíc trénovaného — ten se nabídne jako výchozí. */
function groupByProgram(points: ProgressPoint[]): ProgramTab[] {
  const map = new Map<string, ProgramTab>()

  for (const point of points) {
    const key = programKey(point)
    const existing = map.get(key)
    if (existing) {
      existing.count += 1
    } else {
      map.set(key, { key, name: point.programName ?? 'Bez programu', count: 1 })
    }
  }

  return [...map.values()].sort((a, b) => b.count - a.count)
}

interface DayAggregate {
  day: string
  sessions: number
  avgScore: number
}

/**
 * Denní průměry z vybraných sérií. Počítají se na klientovi, protože server
 * o zvolené záložce neví a agregoval by přes všechny programy dohromady.
 */
function aggregateByDay(points: ProgressPoint[]): DayAggregate[] {
  const map = new Map<string, { sum: number; count: number }>()

  for (const point of points) {
    const day = point.finishedAt.slice(0, 10)
    const bucket = map.get(day) ?? { sum: 0, count: 0 }
    bucket.sum += point.score
    bucket.count += 1
    map.set(day, bucket)
  }

  return [...map.entries()]
    .map(([day, { sum, count }]) => ({ day, sessions: count, avgScore: sum / count }))
    .sort((a, b) => a.day.localeCompare(b.day))
}
